import { ClockCircleOutlined } from '@ant-design/icons';
import { ReactComponent as BillingDarkImage } from '@src/assets/images/billing-dark.svg';
import { ReactComponent as BillingImage } from '@src/assets/images/billing.svg';
import { PageWrapper } from '@src/components';
import { ListItemSkeleton } from '@src/components/Loading/ListItemSkeleton';
import { ResponsiveModal } from '@src/components/ResponsiveModal';
import {
  useLazyGetNotificationsQuery,
  useMarkAsReadMutation,
} from '@src/services/notification/notificationApiSlice';
import { Notification } from '@src/services/notification/notificationApiSliceTypes';
import { THEME } from '@src/store/layout/layoutSlice';
import { RootState } from '@src/store/store';
import { List, Pagination } from 'antd';
import clsx from 'clsx';
import { NotificationStatus } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { PaginationStyles } from './styles';

type NotificationListProps = {
  showHeader?: boolean;
};
export function NotificationList({
  showHeader = false,
}: NotificationListProps) {
  const { t } = useTranslation();
  const activeTheme = useSelector((state: RootState) => state.layout.theme);

  const [page, setPage] = useState(1);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);

  const [getNotificationList, { data, isLoading }] =
    useLazyGetNotificationsQuery();
  const [markAsRead] = useMarkAsReadMutation();

  const notifications = data?.data || [];

  useEffect(() => {
    getNotificationList({ page, limit: 10 });
  }, [page]);

  const handleNotificationClick = async (item: Notification) => {
    setSelectedNotification(item);

    if (!item.read) {
      try {
        await markAsRead({ id: item.id });
        getNotificationList({ page, limit: 10 });
      } catch (err) {
        console.error('Failed to mark notification as read', err);
      }
    }
  };
  if (isLoading) return <ListItemSkeleton />;

  return (
    <PageWrapper>
      <div
        className={clsx(
          'max-w-xl mx-auto max-h-[80vh] md:overflow-y-auto p-4',
          '[&_.ant-empty-description]:text-wl-secondary',
          '[&_.ant-pagination-item-link]:text-wl-secondary'
        )}
      >
        {showHeader && (
          <div className='flex justify-between text-wl-on-surface'>
            <span className='text-M-20'>Notifications</span>
          </div>
        )}

        {notifications.length > 0 ? (
          <div>
            <List
              itemLayout='horizontal'
              dataSource={notifications}
              renderItem={(item) => (
                <button
                  type='button'
                  className='cursor-pointer p-3 rounded-2xl mb-4 bg-wl-background text-wl-on-background'
                  onClick={() => handleNotificationClick(item)}
                  aria-label={item.title}
                >
                  <div className='flex justify-between items-start text-left gap-2'>
                    <div>
                      <div className='font-medium'>{item.title}</div>
                      <div className='text-wl-on-surface opacity-50 text-xs flex items-center gap-1 mt-1'>
                        <ClockCircleOutlined className='text-[10px]' />
                        {item.timestamp}
                      </div>
                    </div>
                    {!item.read && (
                      <div>
                        <span className='inline-block h-2 w-2 rounded-full bg-red-500 mt-1' />
                      </div>
                    )}
                  </div>
                </button>
              )}
            />
            <PaginationStyles>
              <Pagination
                current={data?.page || 1}
                pageSize={data?.limit || 10}
                total={data?.total || 0}
                onChange={(newPage) => setPage(newPage)}
                showSizeChanger={false}
              />
            </PaginationStyles>
          </div>
        ) : (
          <div className='w-fit items-center justify-center flex flex-col gap-4 py-12'>
            {activeTheme === THEME.LIGHT ? (
              <BillingImage className='w-full' />
            ) : (
              <BillingDarkImage className='w-full' />
            )}

            <h2 className='text-B-20 text-wl-on-surface'>No Notifications</h2>

            <p className='text-M-18 text-center text-wl-on-surface'>
              There are no notifications at the moment
            </p>
          </div>
        )}
        {selectedNotification && (
          <ResponsiveModal
            isOpen={!!selectedNotification}
            title={t('pages.notification.detailsTitle')}
            headerIcon={<NotificationStatus variant='Bulk' />}
            content={
              <div className='bg-wl-background text-wl-on-background p-4 rounded-xl'>
                <p>{selectedNotification?.description}</p>
                <div className='text-xs text-wl-on-surface opacity-50 mt-4'>
                  Created at:{' '}
                  {new Date(
                    selectedNotification?.createdAt || ''
                  ).toLocaleString()}
                </div>
              </div>
            }
            onClose={() => setSelectedNotification(null)}
          />
        )}
      </div>
    </PageWrapper>
  );
}
